// FAQ globale — 20 questions structurées correspondant à la section 19
// du brief SEO/GEO. Rendue avec accordéons + JSON-LD FAQPage pour que
// les LLM extraient directement les paires question/réponse.
//
// Chaque réponse est autonome (ne dépend pas d'un scroll ou d'un lien
// pour être comprise) et courte (2–4 phrases). Le maillage sortant est
// concentré en fin de FAQ pour ne pas polluer le schema.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import Layout from "@/components/Layout";
import SEO, { faqPageJsonLd, geovinumOrganizationJsonLd } from "@/components/SEO";

const FAQ = [
  {
    q: "Qu'est-ce que geoVINUM ?",
    a: "geoVINUM est une plateforme de cartographie viticole, de données et d'apprentissage du vin. Elle regroupe cinq produits complémentaires : Maps (cartes physiques et numériques), Explorer (base d'appellations, cépages, producteurs), Academy (parcours de formation alignés WSET), AI (assistant conversationnel spécialisé) et Data / Interactive (cartes intégrables pour partenaires).",
  },
  {
    q: "À quoi sert geoVINUM ?",
    a: "geoVINUM sert à comprendre la géographie du vin — situer une appellation, visualiser un vignoble, apprendre les cépages associés à une région, préparer une certification WSET ou sommellerie. C'est aussi un outil de décoration œnologique (posters de vignobles) et un outil B2B (cartes interactives intégrables sur sites d'écoles ou de domaines).",
  },
  {
    q: "Qu'est-ce qu'une carte viticole ?",
    a: "Une carte viticole représente la géographie d'une région productrice de vin : le contour des appellations, les climats et lieux-dits documentés, le relief, les cours d'eau. Elle permet de lire un vignoble visuellement plutôt que par une liste, ce qui aide à mémoriser les hiérarchies (région → sous-région → appellation → cru).",
  },
  {
    q: "Où trouver une carte des vignobles du monde ?",
    a: "Sur geoVINUM, la page Boutique regroupe les cartes des principales régions viticoles du monde (Bourgogne, Bordeaux, Rhône, Champagne, Italie, Espagne, Portugal, Nouvelle-Zélande, etc.) en format imprimé haute qualité et téléchargeable.",
  },
  {
    q: "Où trouver une carte des appellations ?",
    a: "Chaque carte geoVINUM représente les appellations d'une région donnée avec leur hiérarchie officielle. Pour explorer les appellations sans acheter, la section Explorer propose une fiche par appellation (Chablis, Meursault, Pauillac, etc.) reliée à sa région, ses cépages et ses cartes.",
  },
  {
    q: "Comment apprendre la géographie du vin ?",
    a: "geoVINUM Academy propose des parcours d'apprentissage structurés par région et par référentiel (WSET D3, D4, D5). Chaque parcours combine cartes, fiches Explorer et vidéos. La page Géographie du vin explique les fondamentaux (climat, sols, altitude, systèmes d'appellation).",
  },
  {
    q: "Comment comprendre les terroirs ?",
    a: "Le terroir croise trois facteurs : le sol (nature géologique, drainage), le climat (température, précipitations, ensoleillement) et l'exposition (altitude, orientation). geoVINUM documente le terroir de chaque appellation dans sa fiche Explorer et via la page dédiée /terroirs-viticoles.",
  },
  {
    q: "Quelles régions viticoles sont couvertes ?",
    a: "geoVINUM couvre les principales régions du Vieux Monde (France, Italie, Espagne, Portugal, Allemagne, Autriche, Hongrie) et du Nouveau Monde (États-Unis, Argentine, Chili, Afrique du Sud, Australie, Nouvelle-Zélande, Canada). La liste s'enrichit en continu — la page Explorer donne l'état du catalogue.",
  },
  {
    q: "Quelles appellations sont couvertes ?",
    a: "geoVINUM référence plus de 200 appellations viticoles avec fiche dédiée (contour cartographique, cépages, sous-régions, terroirs). La liste inclut les appellations françaises AOC/AOP, les DOC et DOCG italiennes, les DO espagnoles, les AVA américaines et les principales appellations du Nouveau Monde.",
  },
  {
    q: "Quelles données propose geoVINUM ?",
    a: "geoVINUM structure quatre familles de données : les entités géographiques (pays, régions, sous-régions, appellations avec leurs relations parent-enfant), les cépages (avec profils aromatiques et régions de prédilection), les producteurs et les cartes (données cartographiques téléchargeables via nos cartes physiques). Ces données alimentent l'ensemble des produits.",
  },
  {
    q: "geoVINUM propose-t-il une cartographie interactive ?",
    a: "Oui, via geoVINUM Interactive et l'iframe intégrable. Les partenaires B2B (écoles de vin, domaines, médias) peuvent embarquer les cartes interactives geoVINUM directement dans leur site pour permettre à leurs utilisateurs d'explorer une région appellation par appellation.",
  },
  {
    q: "Qu'est-ce que geoVINUM Explorer ?",
    a: "Explorer est la base éditoriale et exploratoire de geoVINUM : une fiche par entité viticole (appellation, cépage, producteur, article pédagogique), reliée à ses parents géographiques et à ses cartes. Explorer sert de source de vérité pour toute la plateforme — un moteur de connaissance plutôt qu'un blog.",
  },
  {
    q: "Qu'est-ce que geoVINUM Academy ?",
    a: "Academy est le pilier apprentissage : des parcours vidéo et fichés par région ou par certification (WSET Levels 3, 4, 5, sommellerie). Chaque parcours croise cartes Maps + fiches Explorer + quiz pour mémoriser les appellations et leurs cépages.",
  },
  {
    q: "Qu'est-ce que geoVINUM app ?",
    a: "geoVINUM app est un assistant conversationnel spécialisé vin, ancré sur les données Explorer et les cartes. Il répond factuellement à des questions comme « Dans quelle région se trouve Meursault ? », « Quels cépages dans Chablis Premier Cru ? » ou « Quelle carte pour préparer WSET Level 4 France ? ».",
  },
  {
    q: "À qui s'adresse geoVINUM ?",
    a: "geoVINUM sert quatre publics principaux : amateurs qui veulent comprendre le vin visuellement, étudiants en cours de certification (WSET, sommellerie, œnologie), professionnels (sommeliers, cavistes, journalistes, exportateurs) et institutions (écoles du vin, domaines qui intègrent nos cartes en marque blanche).",
  },
  {
    q: "geoVINUM est-il adapté aux étudiants en vin ?",
    a: "Oui — les parcours Academy sont alignés sur les référentiels WSET (D3, D4, D5) et sommellerie, avec cartes de révision, quiz et fiches Explorer croisées. Les cartes Maps servent également d'outil de mémorisation visuelle.",
  },
  {
    q: "geoVINUM est-il utile aux sommeliers ?",
    a: "Oui. Les sommeliers utilisent geoVINUM pour préparer les certifications sommellerie, illustrer une carte des vins, ou former leurs équipes. Les cartes physiques trouvent également leur place en salle et dans les caves.",
  },
  {
    q: "geoVINUM est-il utile aux cavistes ?",
    a: "Oui. Les cartes geoVINUM sont utilisées en boutique pour situer visuellement les vins vendus et créer des discussions avec les clients. L'outil AI aide aussi à répondre rapidement aux questions techniques.",
  },
  {
    q: "geoVINUM peut-il être utilisé pour apprendre les appellations ?",
    a: "C'est un de ses usages principaux. La combinaison Maps + Explorer + Academy permet de mémoriser visuellement les appellations, leurs sous-régions, leurs cépages et leurs terroirs. La logique du site est justement de rendre la géographie explicite plutôt que de la lister.",
  },
  {
    q: "Qui a créé geoVINUM ?",
    a: "geoVINUM est édité par EtOH, entreprise spécialisée dans les technologies pour la filière vin. L'équipe éditoriale et cartographique combine expertises œnologique (WSET Diplomas, sommellerie) et cartographique.",
  },
];

const FAQPage = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <Layout testId="page-faq">
      <SEO
        title="FAQ — geoVINUM"
        description="Réponses aux 20 questions les plus fréquentes sur geoVINUM : cartographie viticole, appellations, Academy, AI, terroirs, publics concernés."
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "FAQ", url: "/faq" },
        ]}
        jsonLdList={[
          faqPageJsonLd(FAQ),
          geovinumOrganizationJsonLd(),
        ]}
      />

      <section className="bg-[color:var(--gv-mist)] py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">Foire aux questions</div>
          <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mb-4" data-testid="faq-title">
            geoVINUM — questions fréquentes
          </h1>
          <p className="text-[15px] text-[color:var(--gv-ink)]/70 leading-relaxed max-w-2xl">
            Vingt réponses courtes et factuelles pour comprendre ce qu'est geoVINUM,
            ce que la plateforme couvre et à qui elle s'adresse.
          </p>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-6 space-y-2">
          {FAQ.map((item, idx) => {
            const open = openIdx === idx;
            return (
              <div
                key={idx}
                className="border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden"
                data-testid={`faq-item-${idx}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : idx)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[color:var(--gv-mist)]/40 transition-colors"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="font-display text-[16px] text-[color:var(--gv-ink)]">{item.q}</span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-[color:var(--gv-pink)] transition-transform ${open ? "rotate-180" : ""}`}
                  />
                </button>
                {open && (
                  <div className="px-5 pb-5 text-[14px] text-[color:var(--gv-ink)]/85 leading-relaxed border-t border-[color:var(--gv-line)] pt-3">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="max-w-3xl mx-auto px-6 mt-12 pt-8 border-t border-[color:var(--gv-line)]">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">Aller plus loin</div>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[13px]">
            <li><Link to="/blog" className="text-[color:var(--gv-pink)] hover:underline">Explorer</Link></li>
            <li><Link to="/shop" className="text-[color:var(--gv-pink)] hover:underline">Boutique · Cartes</Link></li>
            <li><Link to="/academy" className="text-[color:var(--gv-pink)] hover:underline">Academy</Link></li>
            <li><Link to="/geovinum-ai" className="text-[color:var(--gv-pink)] hover:underline">geoVINUM app</Link></li>
            <li><Link to="/iframe" className="text-[color:var(--gv-pink)] hover:underline">Cartes intégrables</Link></li>
            <li><Link to="/about" className="text-[color:var(--gv-pink)] hover:underline">À propos</Link></li>
          </ul>
        </div>
      </section>
    </Layout>
  );
};

export default FAQPage;
